// src/services/clipViews.js
// MyClips Faz 1B Görev 1 — İstemci tarafı izlenme (view) telemetrisi helper'ı.
//
// Sunucu (functions/src/clips_views.ts) `logClipView` callable'ını çağırır.
// Yerel session'da aynı clipId için iki kez aynı event'i gönderilmesini
// engeller (network spam + gereksiz transaction yükü).
//
// Kullanım:
//   import { reportClipView, reportClipComplete } from "./services/clipViews";
//   reportClipView(clipId, watchedMsSoFar);       // ≥2s izlenince bir kez
//   reportClipComplete(clipId, watchedMsTotal);   // ≥%90 izlenince bir kez
//
// Sunucu tarafında saatlik dedup zaten var; burada session dedup "bu sekmede
// aynı isteği iki kez gönderme" garantisi için ek kalkandır.

import { httpsCallable } from "firebase/functions";
import { functions, auth } from "../firebase";
// FAZ 5 GELİŞTİRME 1 — Device fingerprint helper (sessionStorage cache;
// ilk çağrıda hesap + cache, sonraki çağrılarda anında dönüş). Payload'a
// opsiyonel `deviceId` olarak eklenir; server tarafı geriye dönük uyumlu
// (eski client'lar bu alanı taşımaz → server view_device rate-limit'i
// no-op döner).
import { getDeviceFingerprint } from "../utils/deviceFingerprint";

// Bu sekme ömrü boyunca hangi clipId'ler için hangi event'i gönderdik?
// - sentView: play+2s tetiği bir kez gönderildi
// - sentComplete: ≥%90 tetiği bir kez gönderildi
const sentView = new Set<string>();
const sentComplete = new Set<string>();

// Son çağrı zamanlarını tut — aynı saniyede ard arda çağrı olmasın
const lastCallAt = new Map<string, number>();
const MIN_GAP_MS = 1500;

function _canSend(clipId: string): boolean {
  const now = Date.now();
  const last = lastCallAt.get(clipId) || 0;
  if (now - last < MIN_GAP_MS) return false;
  lastCallAt.set(clipId, now);
  return true;
}

function _getCallable() {
  // Her çağrıda yeniden üret: HMR/testler için güvenli
  return httpsCallable(functions, "logClipView");
}

/**
 * Clip izlenme başlangıcını raporla (ilk play + ~2s dwell sonrası).
 * Session içinde aynı clipId için yalnızca bir kez etki eder.
 *
 * @param clipId klip kimliği
 * @param watchedMs o ana kadar izlenen ms (yaklaşık)
 * @param source Sprint 22: keşif yüzeyi (whitelist)
 */
export async function reportClipView(
  clipId: string,
  watchedMs: number = 0,
  source: string | null = null,
): Promise<void> {
  try {
    if (!clipId || typeof clipId !== "string") return;
    if (!auth?.currentUser) return; // Anonim izleyici — telemetri yok
    if (sentView.has(clipId)) return;
    if (!_canSend(clipId)) return;
    sentView.add(clipId);

    // FAZ 5 GELİŞTİRME 1 — Device fingerprint payload'a eklenir. Async
    // ama sessionStorage cache sayesinde 2.+ çağrıda anında döner (~µs).
    // Hesap başarısız olursa "unknown_<base36>" fallback; server tarafı
    // bu pattern'i ayırt edip rate-limit'i no-op tutar.
    const deviceId = await getDeviceFingerprint();

    const call = _getCallable();
    const payload: any = {
      clipId,
      completed: false,
      watchedMs: Math.max(0, Math.floor(watchedMs)),
    };
    if (typeof source === "string" && source) payload.source = source;
    if (typeof deviceId === "string" && deviceId) payload.deviceId = deviceId;
    await call(payload);
  } catch (e) {
    // Sessiz: telemetri kullanıcı deneyimini bloklamamalı
    // Dev'te görmek isterseniz console.warn açabilirsiniz
  }
}

/**
 * Clip'in ≥%90 izlendiği olayını raporla.
 * Session içinde aynı clipId için yalnızca bir kez etki eder.
 *
 * @param clipId klip kimliği
 * @param watchedMs toplam izlenme ms
 * @param source Sprint 22: keşif yüzeyi (whitelist)
 */
export async function reportClipComplete(
  clipId: string,
  watchedMs: number = 0,
  source: string | null = null,
): Promise<void> {
  try {
    if (!clipId || typeof clipId !== "string") return;
    if (!auth?.currentUser) return;
    if (sentComplete.has(clipId)) return;
    if (!_canSend(clipId)) return;
    sentComplete.add(clipId);

    // FAZ 5 GELİŞTİRME 1 — Device fingerprint payload'a eklenir (reportClipView
    // ile aynı yol). reportClipView session'da zaten çağrılmışsa cache hit
    // ile anında döner; complete event'inde maliyet ~0.
    const deviceId = await getDeviceFingerprint();

    const call = _getCallable();
    const payload: any = {
      clipId,
      completed: true,
      watchedMs: Math.max(0, Math.floor(watchedMs)),
    };
    if (typeof source === "string" && source) payload.source = source;
    if (typeof deviceId === "string" && deviceId) payload.deviceId = deviceId;
    await call(payload);
  } catch (e) {
    // Sessiz
  }
}

/**
 * Test/debug: session state'i sıfırla (HMR, unit test, admin test akışları).
 */
export function _resetClipViewsSession() {
  sentView.clear();
  sentComplete.clear();
  lastCallAt.clear();
}
